import type { MisconceptionDefinition } from '../types'

/**
 * TAA — Ontleden sub-registry.
 *
 * Misconcepties rond zinsontleding: onderwerp/gezegde, bijwoordelijke
 * bepalingen, bijzinnen, betrekkelijke bijzinnen, inversie,
 * naamwoordelijk gezegde, en hoofd/bijzin-onderscheid.
 * Families: `TAA-ONT-*`.
 *
 * Onderdeel van de TAA-barrel-splitsing (juni 2026).
 */
export const TAA_ONTLEDEN_MISCONCEPTIONS: MisconceptionDefinition[] = [
  // ─── Onderwerp / gezegde (basis) ────────────────────────────────────────
  {
    id: 'taa.subject-predicate-confused',
    label: 'Onderwerp/gezegde verward',
    module: 'Taalbegrip',
    description: 'Leerling verwert het onderwerp met het gezegde in een zin.',
    typicalFamilies: ['TAA-ONT-GEZEGDE', 'TAA-ONT-BASIS'],
  },
  // ─── Bijwoordelijke bepaling (TAA-ONT-BWB) ─────────────────────────────
  {
    id: 'taa.bwb-plaats-onderwerp-verward',
    label: 'Verwart onderwerp met bijwoordelijke bepaling van plaats',
    module: 'Taalbegrip',
    description:
      'Leerling kiest het onderwerp van de zin als bijwoordelijke bepaling van plaats.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-plaats-pv-verward',
    label: 'Verwart persoonsvorm met bijwoordelijke bepaling',
    module: 'Taalbegrip',
    description:
      'Leerling kiest de persoonsvorm als bijwoordelijke bepaling van plaats.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-plaats-voorzetsel-weggelaten',
    label: 'Laat voorzetsel weg bij plaatsbepaling',
    module: 'Taalbegrip',
    description:
      'Leerling laat het voorzetsel ("op", "in", "naar") weg uit de bijwoordelijke plaatsbepaling.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-tijd-pv-verward',
    label: 'Verwart persoonsvorm met bijwoordelijke bepaling van tijd',
    module: 'Taalbegrip',
    description:
      'Leerling kiest de persoonsvorm als bijwoordelijke bepaling van tijd.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-tijd-onderwerp-verward',
    label: 'Verwart onderwerp met bijwoordelijke bepaling van tijd',
    module: 'Taalbegrip',
    description:
      'Leerling kiest het onderwerp als bijwoordelijke bepaling van tijd.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-tijd-richting-verward',
    label: 'Verwart bijwoordelijke bepaling van richting met tijd',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een bijwoordelijke bepaling van richting ("naar het strand") als bijwoordelijke bepaling van tijd.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-wijze-onderwerp-verward',
    label: 'Verwart onderwerp met bijwoordelijke bepaling van wijze',
    module: 'Taalbegrip',
    description:
      'Leerling kiest het onderwerp als bijwoordelijke bepaling van wijze.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-wijze-pv-verward',
    label: 'Verwart persoonsvorm met bijwoordelijke bepaling van wijze',
    module: 'Taalbegrip',
    description:
      'Leerling kiest de persoonsvorm als bijwoordelijke bepaling van wijze.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-wijze-vzv-verward',
    label: 'Verwart voorzetselvoorwerp met bijwoordelijke bepaling van wijze',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een voorzetselvoorwerp ("aan het project") als bijwoordelijke bepaling van wijze.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-reden-tijd-verward',
    label: 'Verwart bijwoordelijke bepaling van reden met tijd',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een tijdsbepaling waar een reden-bepaling bedoeld is.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-reden-plaats-verward',
    label: 'Verwart bijwoordelijke bepaling van reden met plaats',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een plaatsbepaling waar een reden-bepaling bedoeld is.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-reden-wijze-verward',
    label: 'Verwart bijwoordelijke bepaling van reden met wijze',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een wijze-bepaling waar een reden-bepaling bedoeld is.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-tellen-te-weinig-2',
    label: 'Vindt 2 van de 3 bijwoordelijke bepalingen',
    module: 'Taalbegrip',
    description:
      'Leerling vindt slechts 2 van de 3 bijwoordelijke bepalingen in een zin.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-tellen-te-weinig-1',
    label: 'Vindt slechts 1 bijwoordelijke bepaling',
    module: 'Taalbegrip',
    description:
      'Leerling vindt slechts 1 van de 3 bijwoordelijke bepalingen in een zin.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-tellen-te-veel',
    label: 'Telt onderwerp of pv mee als bijwoordelijke bepaling',
    module: 'Taalbegrip',
    description:
      'Leerling telt het onderwerp of de persoonsvorm mee als bijwoordelijke bepaling.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  // ─── Bijzin (TAA-ONT-BIJZIN) ────────────────────────────────────────────
  {
    id: 'taa.bijzin-hoofdzin-verward',
    label: 'Verwart hoofdzin met bijzin',
    module: 'Taalbegrip',
    description:
      'Leerling kiest de hoofdzin van een samengestelde zin wanneer naar de bijzin gevraagd wordt.',
    typicalFamilies: ['TAA-ONT-BIJZIN'],
  },
  {
    id: 'taa.bijzin-hoofdzin-meegeteld',
    label: 'Telt hoofdzin mee als bijzin',
    module: 'Taalbegrip',
    description:
      'Leerling telt de hoofdzin van een samengestelde zin mee als bijzin.',
    typicalFamilies: ['TAA-ONT-BIJZIN'],
  },
  {
    id: 'taa.bijzin-bepaling-verwart',
    label: 'Verwart bijwoordelijke bepaling met bijzin',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een bijwoordelijke bepaling waar een bijzin bedoeld is.',
    typicalFamilies: ['TAA-ONT-BIJZIN'],
  },
  {
    id: 'taa.bijzin-voegw-maar-onderschikkend',
    label: 'Verwart "maar" (nevenschikkend) met onderschikkend voegwoord',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "maar" een onderschikkend voegwoord is dat een bijzin maakt.',
    typicalFamilies: ['TAA-ONT-BIJZIN', 'TAA-ONT-HOOFDZIN'],
  },
  {
    id: 'taa.bijzin-voegw-en-onderschikkend',
    label: 'Verwart nevenschikkend voegwoord ("en"/"dus") met onderschikkend',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een nevenschikkend voegwoord ("en", "dus") een bijzin maakt.',
    typicalFamilies: ['TAA-ONT-BIJZIN', 'TAA-ONT-HOOFDZIN'],
  },
  {
    id: 'taa.bijzin-voegw-want-omdat-verward',
    label: 'Verwart "want" (nevenschikkend) met "omdat" (onderschikkend)',
    module: 'Taalbegrip',
    description:
      'Leerling verwart "want" (verbindt hoofdzinnen) met "omdat" (maakt bijzin).',
    typicalFamilies: ['TAA-ONT-BIJZIN', 'TAA-ONT-HOOFDZIN'],
  },
  {
    id: 'taa.bijzin-tellen-eentje-gemist',
    label: 'Mist één bijzin bij tellen',
    module: 'Taalbegrip',
    description:
      'Leerling mist één van de aanwezige bijzinnen wanneer er meerdere voegwoorden in de zin staan.',
    typicalFamilies: ['TAA-ONT-BIJZIN'],
  },
  {
    id: 'taa.bijzin-geen-herkend',
    label: 'Herkent slechts één bijzin in zin met meerdere',
    module: 'Taalbegrip',
    description:
      'Leerling herkent slechts één bijzin in een zin die meerdere bijzinnen bevat.',
    typicalFamilies: ['TAA-ONT-BIJZIN'],
  },
  {
    id: 'taa.bijzin-lengte-criterium',
    label: 'Denkt dat lengte bepaalt of iets een bijzin is',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat de lengte van een zin het criterium is voor bijzin of hoofdzin.',
    typicalFamilies: ['TAA-ONT-BIJZIN'],
  },
  {
    id: 'taa.bijzin-positie-criterium',
    label: 'Denkt dat positie bepaalt of iets een bijzin is',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat de positie in de zin (vooraan/achteraan) bepaalt of het een bijzin is.',
    typicalFamilies: ['TAA-ONT-BIJZIN'],
  },
  {
    id: 'taa.bijzin-pv-verward',
    label: 'Denkt dat bijzin geen persoonsvorm/onderwerp heeft',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een bijzin geen persoonsvorm of onderwerp bevat.',
    typicalFamilies: ['TAA-ONT-BIJZIN'],
  },
  {
    id: 'taa.bijzin-zonder-voegwoord',
    label: 'Voegwoord niet vooraan in bijzin geplaatst',
    module: 'Taalbegrip',
    description:
      'Leerling plaatst het voegwoord niet vooraan in de bijzin bij het zelf vormen van een bijzin.',
    typicalFamilies: ['TAA-ONT-BIJZIN'],
  },
  // ─── Betrekkelijke bijzin (TAA-ONT-BETREKKELIJK) ────────────────────────
  {
    id: 'taa.betrekkelijk-voorzetsel-van-verward',
    label: 'Verwart voorzetsel "van" met betrekkelijk voornaamwoord',
    module: 'Taalbegrip',
    description:
      'Leerling kiest het voorzetsel "van" als betrekkelijk voornaamwoord.',
    typicalFamilies: ['TAA-ONT-BETREKKELIJK'],
  },
  {
    id: 'taa.betrekkelijk-voorzetsel-verward',
    label: 'Verwart voorzetsel met betrekkelijk voornaamwoord',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een voorzetsel ("in", "op") als betrekkelijk voornaamwoord.',
    typicalFamilies: ['TAA-ONT-BETREKKELIJK'],
  },
  {
    id: 'taa.betrekkelijk-voorzetsel-weggelaten',
    label: 'Laat voorzetsel weg bij betrekkelijke bijzin',
    module: 'Taalbegrip',
    description:
      'Leerling laat het voorzetsel weg dat bij de betrekkelijke bijzin hoort.',
    typicalFamilies: ['TAA-ONT-BETREKKELIJK'],
  },
  {
    id: 'taa.betrekkelijk-die-dat-de-woord',
    label: 'Gebruikt "dat" bij de-woord (of "die" bij het-woord)',
    module: 'Taalbegrip',
    description:
      'Leerling kiest "dat" bij een de-woord of "die" bij een het-woord enkelvoud.',
    typicalFamilies: ['TAA-ONT-BETREKKELIJK'],
  },
  {
    id: 'taa.betrekkelijk-die-dat-meervoud',
    label: 'Gebruikt "dat" bij meervoud i.p.v. "die"',
    module: 'Taalbegrip',
    description:
      'Leerling gebruikt "dat" bij een meervoudig antecedent waar "die" verplicht is.',
    typicalFamilies: ['TAA-ONT-BETREKKELIJK'],
  },
  {
    id: 'taa.betrekkelijk-die-dat-altijd-dat',
    label: 'Gebruikt overal "dat" zonder onderscheid',
    module: 'Taalbegrip',
    description:
      'Leerling vult overal "dat" in zonder onderscheid tussen de-/het-woord en enkelvoud/meervoud.',
    typicalFamilies: ['TAA-ONT-BETREKKELIJK'],
  },
  {
    id: 'taa.betrekkelijk-onderschikkend-verward',
    label: 'Verwart onderschikkende bijzin met betrekkelijke bijzin',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een onderschikkende bijzin ("omdat"-, "als"-bijzin) met een betrekkelijke bijzin.',
    typicalFamilies: ['TAA-ONT-BETREKKELIJK'],
  },
  {
    id: 'taa.betrekkelijk-nevenschikkend-verward',
    label: 'Verwart nevenschikkende zin met betrekkelijke bijzin',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een nevenschikkende zin ("en"-zin) met een betrekkelijke bijzin.',
    typicalFamilies: ['TAA-ONT-BETREKKELIJK'],
  },
  {
    id: 'taa.betrekkelijk-hoofdzin-als-bijzin',
    label: 'Kiest hoofdzin-element als betrekkelijke bijzin',
    module: 'Taalbegrip',
    description:
      'Leerling kiest het onderwerp of een ander element van de hoofdzin als betrekkelijke bijzin.',
    typicalFamilies: ['TAA-ONT-BETREKKELIJK'],
  },
  {
    id: 'taa.betrekkelijk-gezegde-als-bijzin',
    label: 'Kiest gezegde van hoofdzin als betrekkelijke bijzin',
    module: 'Taalbegrip',
    description:
      'Leerling kiest het gezegde van de hoofdzin als betrekkelijke bijzin.',
    typicalFamilies: ['TAA-ONT-BETREKKELIJK'],
  },
  // ─── Inversie (TAA-ONT-INVERSIE) ────────────────────────────────────────
  {
    id: 'taa.inversie-normale-volgorde-a',
    label: 'Kiest zin met normale volgorde (onderwerp vooraan, optie a)',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een zin met onderwerp-eerst volgorde waar inversie gevraagd wordt.',
    typicalFamilies: ['TAA-ONT-INVERSIE'],
  },
  {
    id: 'taa.inversie-normale-volgorde-c',
    label: 'Kiest zin met normale volgorde (onderwerp vooraan, optie c)',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een zin met onderwerp-eerst volgorde waar inversie gevraagd wordt.',
    typicalFamilies: ['TAA-ONT-INVERSIE'],
  },
  {
    id: 'taa.inversie-normale-volgorde-d',
    label: 'Kiest zin met normale volgorde (onderwerp vooraan, optie d)',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een zin met onderwerp-eerst volgorde waar inversie gevraagd wordt.',
    typicalFamilies: ['TAA-ONT-INVERSIE'],
  },
  {
    id: 'taa.inversie-onderwerp-bijwoord',
    label: 'Verwart bijwoord met onderwerp bij inversie',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een bijwoordelijke bepaling van tijd als onderwerp in een zin met inversie.',
    typicalFamilies: ['TAA-ONT-INVERSIE'],
  },
  {
    id: 'taa.inversie-onderwerp-lv',
    label: 'Verwart lijdend voorwerp met onderwerp bij inversie',
    module: 'Taalbegrip',
    description:
      'Leerling kiest het lijdend voorwerp als onderwerp in een zin met inversie.',
    typicalFamilies: ['TAA-ONT-INVERSIE'],
  },
  {
    id: 'taa.inversie-onderwerp-werkwoord',
    label: 'Verwart voltooid deelwoord met onderwerp bij inversie',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een voltooid deelwoord als onderwerp in een zin met inversie.',
    typicalFamilies: ['TAA-ONT-INVERSIE'],
  },
  {
    id: 'taa.inversie-functie-verplicht',
    label: 'Denkt dat inversie verplicht is',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat inversie in elke zin verplicht is.',
    typicalFamilies: ['TAA-ONT-INVERSIE'],
  },
  {
    id: 'taa.inversie-functie-positie',
    label: 'Denkt dat onderwerp altijd achteraan moet bij inversie',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat het onderwerp bij inversie altijd helemaal achteraan in de zin staat.',
    typicalFamilies: ['TAA-ONT-INVERSIE'],
  },
  {
    id: 'taa.inversie-functie-korter',
    label: 'Denkt dat inversie de zin korter maakt',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat inversie als functie heeft om de zin in te korten.',
    typicalFamilies: ['TAA-ONT-INVERSIE'],
  },
  {
    id: 'taa.inversie-toepassen-pv-vooraan',
    label: 'Begint met persoonsvorm bij inversie i.p.v. ander zinsdeel',
    module: 'Taalbegrip',
    description:
      'Leerling begint de zin met de persoonsvorm i.p.v. een ander zinsdeel (bw. bepaling, lijdend voorwerp).',
    typicalFamilies: ['TAA-ONT-INVERSIE'],
  },
  {
    id: 'taa.inversie-toepassen-lv-fout',
    label: 'Zet lijdend voorwerp vooraan zonder juiste woordvolgorde',
    module: 'Taalbegrip',
    description:
      'Leerling zet het lijdend voorwerp vooraan maar past de woordvolgorde niet correct aan.',
    typicalFamilies: ['TAA-ONT-INVERSIE'],
  },
  {
    id: 'taa.inversie-toepassen-geen-wisseling',
    label: 'Voegt zinsdeel in zonder woordvolgorde aan te passen',
    module: 'Taalbegrip',
    description:
      'Leerling voegt een zinsdeel vooraan toe zonder de woordvolgorde (pv-onderwerp wissel) aan te passen.',
    typicalFamilies: ['TAA-ONT-INVERSIE'],
  },
  // ─── Naamwoordelijk gezegde (TAA-ONT-NAAMWOORDELIJK) ────────────────────
  {
    id: 'taa.nwdeel-onderwerp-verward',
    label: 'Verwart onderwerp met naamwoordelijk deel',
    module: 'Taalbegrip',
    description: 'Leerling kiest het onderwerp als naamwoordelijk deel.',
    typicalFamilies: ['TAA-ONT-NAAMWOORDELIJK'],
  },
  {
    id: 'taa.nwdeel-koppelww-verward',
    label: 'Verwart koppelwerkwoord met naamwoordelijk deel',
    module: 'Taalbegrip',
    description:
      'Leerling kiest het koppelwerkwoord zelf als naamwoordelijk deel.',
    typicalFamilies: ['TAA-ONT-NAAMWOORDELIJK'],
  },
  {
    id: 'taa.nwdeel-koppelww-plus-onderwerp',
    label: 'Neemt koppelwerkwoord en onderwerp samen als naamwoordelijk deel',
    module: 'Taalbegrip',
    description:
      'Leerling neemt het koppelwerkwoord plus het onderwerp samen als naamwoordelijk deel.',
    typicalFamilies: ['TAA-ONT-NAAMWOORDELIJK'],
  },
  {
    id: 'taa.nwdeel-koppelww-handeling-lopen',
    label: 'Verwart zelfstandig werkwoord "lopen" met koppelwerkwoord',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat "lopen" een koppelwerkwoord is.',
    typicalFamilies: ['TAA-ONT-NAAMWOORDELIJK'],
  },
  {
    id: 'taa.nwdeel-koppelww-handeling-schrijven',
    label: 'Verwart zelfstandig werkwoord "schrijven" met koppelwerkwoord',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat "schrijven" een koppelwerkwoord is.',
    typicalFamilies: ['TAA-ONT-NAAMWOORDELIJK'],
  },
  {
    id: 'taa.nwdeel-koppelww-handeling-gooien',
    label: 'Verwart zelfstandig werkwoord "gooien" met koppelwerkwoord',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat "gooien" een koppelwerkwoord is.',
    typicalFamilies: ['TAA-ONT-NAAMWOORDELIJK'],
  },
  {
    id: 'taa.nwdeel-lv-verward',
    label: 'Verwart lijdend voorwerp met naamwoordelijk deel',
    module: 'Taalbegrip',
    description: 'Leerling kiest een lijdend voorwerp als naamwoordelijk deel.',
    typicalFamilies: ['TAA-ONT-NAAMWOORDELIJK'],
  },
  {
    id: 'taa.nwdeel-bwb-verward',
    label: 'Verwart bijwoordelijke bepaling met naamwoordelijk deel',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een bijwoordelijke bepaling als naamwoordelijk deel.',
    typicalFamilies: ['TAA-ONT-NAAMWOORDELIJK'],
  },
  {
    id: 'taa.nwdeel-geen-koppelww-herkend',
    label: 'Herkent niet welke zin een koppelwerkwoord bevat',
    module: 'Taalbegrip',
    description:
      'Leerling herkent niet welke zin een koppelwerkwoord (en dus een naamwoordelijk deel) bevat.',
    typicalFamilies: ['TAA-ONT-NAAMWOORDELIJK'],
  },
  {
    id: 'taa.nwdeel-bezittelijk-verward',
    label: 'Verwart bezittelijk voornaamwoord met naamwoordelijk deel',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een bezittelijk voornaamwoord ("mijn") als naamwoordelijk deel.',
    typicalFamilies: ['TAA-ONT-NAAMWOORDELIJK'],
  },
  // ─── Hoofdzin / samengestelde zin (TAA-ONT-HOOFDZIN) ────────────────────
  {
    id: 'taa.hoofdzin-een-lange-zin',
    label: 'Ziet samengestelde zin als één hoofdzin',
    module: 'Taalbegrip',
    description:
      'Leerling beschouwt een samengestelde zin met twee persoonsvormen als één hoofdzin.',
    typicalFamilies: ['TAA-ONT-HOOFDZIN'],
  },
  {
    id: 'taa.voegwoord-als-zin',
    label: 'Telt voegwoord als aparte zin',
    module: 'Taalbegrip',
    description: 'Leerling telt het voegwoord ("en", "of") als een aparte zin.',
    typicalFamilies: ['TAA-ONT-HOOFDZIN'],
  },
  {
    id: 'taa.hoofdzin-niet-herkend',
    label: 'Herkent hoofdzinnen niet als zelfstandig',
    module: 'Taalbegrip',
    description:
      'Leerling herkent niet dat de delen van een samengestelde zin zelfstandig kunnen bestaan.',
    typicalFamilies: ['TAA-ONT-HOOFDZIN'],
  },
  {
    id: 'taa.want-omdat-verward',
    label: 'Verwart "want" (nevenschikkend) met "omdat" (onderschikkend)',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "want" en "omdat" volledig uitwisselbaar zijn.',
    typicalFamilies: ['TAA-ONT-HOOFDZIN'],
  },
  {
    id: 'taa.dat-nevenschikkend',
    label: 'Denkt dat "dat" nevenschikkend is',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat het voegwoord "dat" nevenschikkend is i.p.v. onderschikkend.',
    typicalFamilies: ['TAA-ONT-HOOFDZIN'],
  },
  {
    id: 'taa.als-nevenschikkend',
    label: 'Denkt dat "als" nevenschikkend is',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat het voegwoord "als" nevenschikkend is i.p.v. onderschikkend.',
    typicalFamilies: ['TAA-ONT-HOOFDZIN'],
  },
  {
    id: 'taa.omdat-twee-hoofdzinnen',
    label: 'Denkt dat "omdat" twee hoofdzinnen verbindt',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "omdat" twee hoofdzinnen verbindt i.p.v. een bijzin in te leiden.',
    typicalFamilies: ['TAA-ONT-HOOFDZIN'],
  },
  {
    id: 'taa.die-twee-hoofdzinnen',
    label: 'Denkt dat "die" twee hoofdzinnen verbindt',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat het betrekkelijk voornaamwoord "die" twee hoofdzinnen verbindt.',
    typicalFamilies: ['TAA-ONT-HOOFDZIN'],
  },
  {
    id: 'taa.als-twee-hoofdzinnen',
    label: 'Denkt dat "als" twee hoofdzinnen verbindt',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "als" twee hoofdzinnen verbindt i.p.v. een bijzin in te leiden.',
    typicalFamilies: ['TAA-ONT-HOOFDZIN'],
  },
  {
    id: 'taa.hoofdzin-lengte-verschil',
    label: 'Denkt dat lengte het verschil tussen hoofd- en bijzin bepaalt',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat de lengte van een zin het verschil tussen hoofdzin en bijzin bepaalt.',
    typicalFamilies: ['TAA-ONT-HOOFDZIN'],
  },
  {
    id: 'taa.hoofdzin-geen-pv',
    label: 'Denkt dat hoofdzin geen persoonsvorm heeft',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat een hoofdzin geen persoonsvorm bevat.',
    typicalFamilies: ['TAA-ONT-HOOFDZIN'],
  },
  {
    id: 'taa.bijzin-altijd-vooraan',
    label: 'Denkt dat bijzin altijd vooraan in de zin staat',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een bijzin altijd vooraan in de samengestelde zin staat.',
    typicalFamilies: ['TAA-ONT-HOOFDZIN'],
  },
  // ─── TAA-ONT-BASIS — seed-reconciliatie (Phase B, juni 2026) ────────────
  {
    id: 'taa.ont-pv-als-onderwerp',
    label: 'Persoonsvorm aangezien voor onderwerp',
    module: 'Taalbegrip',
    description:
      'Leerling kiest de persoonsvorm als onderwerp van de zin (verwart wie/wat-vraag met werkwoord).',
    typicalFamilies: ['TAA-ONT-BASIS'],
  },
  {
    id: 'taa.ont-bijwoord-als-onderwerp',
    label: 'Bijwoord aangezien voor onderwerp',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een bijwoord (tijd/plaats/wijze) als onderwerp van de zin.',
    typicalFamilies: ['TAA-ONT-BASIS'],
  },
  {
    id: 'taa.ont-onderwerp-als-pv',
    label: 'Onderwerp aangezien voor persoonsvorm',
    module: 'Taalbegrip',
    description:
      'Leerling kiest het onderwerp als persoonsvorm (kent persoonsvorm-criteria niet toe).',
    typicalFamilies: ['TAA-ONT-BASIS'],
  },
  {
    id: 'taa.ont-lv-als-pv',
    label: 'Lijdend voorwerp aangezien voor persoonsvorm',
    module: 'Taalbegrip',
    description: 'Leerling kiest het lijdend voorwerp als persoonsvorm.',
    typicalFamilies: ['TAA-ONT-BASIS'],
  },
  {
    id: 'taa.ont-eerste-woord-als-onderwerp',
    label: 'Eerste woord altijd als onderwerp gezien',
    module: 'Taalbegrip',
    description:
      'Leerling kiest reflexmatig het eerste woord van de zin als onderwerp, ook bij inversie of bijwoordelijke aanloop.',
    typicalFamilies: ['TAA-ONT-BASIS'],
  },
  {
    id: 'taa.ont-bwb-als-onderwerp',
    label: 'Bijwoordelijke bepaling aangezien voor onderwerp',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een bijwoordelijke bepaling als onderwerp van de zin.',
    typicalFamilies: ['TAA-ONT-BASIS'],
  },
  {
    id: 'taa.ont-vd-als-pv',
    label: 'Voltooid deelwoord aangezien voor persoonsvorm',
    module: 'Taalbegrip',
    description:
      'Leerling kiest het voltooid deelwoord als persoonsvorm in plaats van het hulpwerkwoord.',
    typicalFamilies: ['TAA-ONT-BASIS'],
  },
  {
    id: 'taa.ont-bijwoord-als-pv',
    label: 'Bijwoord aangezien voor persoonsvorm',
    module: 'Taalbegrip',
    description: 'Leerling kiest een bijwoord als persoonsvorm.',
    typicalFamilies: ['TAA-ONT-BASIS'],
  },
  {
    id: 'taa.ont-onderwerp-als-lv',
    label: 'Onderwerp aangezien voor lijdend voorwerp',
    module: 'Taalbegrip',
    description:
      'Leerling kiest het onderwerp als lijdend voorwerp van de zin.',
    typicalFamilies: ['TAA-ONT-BASIS'],
  },
  {
    id: 'taa.ont-pv-als-lv',
    label: 'Persoonsvorm aangezien voor lijdend voorwerp',
    module: 'Taalbegrip',
    description:
      'Leerling kiest de persoonsvorm als lijdend voorwerp van de zin.',
    typicalFamilies: ['TAA-ONT-BASIS'],
  },
  // ─── TAA-ONT-LV-MV — seed-reconciliatie (Phase B, juni 2026) ────────────
  {
    id: 'taa.lv-mv-verwarring',
    label: 'Lijdend en meewerkend voorwerp verwisseld',
    module: 'Taalbegrip',
    description:
      'Leerling verwart het lijdend voorwerp (LV) met het meewerkend voorwerp (MV) of andersom.',
    typicalFamilies: ['TAA-ONT-LV-MV'],
  },
  {
    id: 'taa.onderwerp-als-lv',
    label: 'Onderwerp aangezien voor LV',
    module: 'Taalbegrip',
    description: 'Leerling kiest het onderwerp als lijdend voorwerp.',
    typicalFamilies: ['TAA-ONT-LV-MV'],
  },
  {
    id: 'taa.pv-als-zinsdeel',
    label: 'Persoonsvorm als LV/MV-zinsdeel gekozen',
    module: 'Taalbegrip',
    description:
      'Leerling kiest de persoonsvorm als lijdend of meewerkend voorwerp.',
    typicalFamilies: ['TAA-ONT-LV-MV'],
  },
  {
    id: 'taa.onderwerp-als-mv',
    label: 'Onderwerp aangezien voor MV',
    module: 'Taalbegrip',
    description: 'Leerling kiest het onderwerp als meewerkend voorwerp.',
    typicalFamilies: ['TAA-ONT-LV-MV'],
  },
  {
    id: 'taa.bwb-als-lv',
    label: 'Bijwoordelijke bepaling aangezien voor LV',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een bijwoordelijke bepaling als lijdend voorwerp.',
    typicalFamilies: ['TAA-ONT-LV-MV'],
  },
  {
    id: 'taa.mv-herkenning-zonder-overdracht',
    label: 'MV vermoed zonder overdracht-werkwoord',
    module: 'Taalbegrip',
    description:
      'Leerling vermoedt een meewerkend voorwerp zonder dat er een overdracht-werkwoord (geven, sturen, vertellen, …) in de zin staat.',
    typicalFamilies: ['TAA-ONT-LV-MV'],
  },
  {
    id: 'taa.bwb-als-mv',
    label: 'Bijwoordelijke bepaling aangezien voor MV',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een bijwoordelijke bepaling als meewerkend voorwerp.',
    typicalFamilies: ['TAA-ONT-LV-MV'],
  },
  // ─── TAA-ONT-GEZEGDE — seed-reconciliatie (Phase B, juni 2026) ──────────
  {
    id: 'taa.gez-onderwerp-als-gezegde',
    label: 'Onderwerp aangemerkt als gezegde',
    module: 'Taalbegrip',
    description:
      'Leerling markeert het onderwerp van de zin als (deel van het) gezegde.',
    typicalFamilies: ['TAA-ONT-GEZEGDE'],
  },
  {
    id: 'taa.gez-bwb-als-gezegde',
    label: 'Bijwoordelijke bepaling aangemerkt als gezegde',
    module: 'Taalbegrip',
    description:
      'Leerling markeert een bijwoordelijke bepaling als (deel van het) gezegde.',
    typicalFamilies: ['TAA-ONT-GEZEGDE'],
  },
  {
    id: 'taa.gez-lv-als-gezegde',
    label: 'Lijdend voorwerp aangemerkt als gezegde',
    module: 'Taalbegrip',
    description:
      'Leerling markeert het lijdend voorwerp als (deel van het) gezegde.',
    typicalFamilies: ['TAA-ONT-GEZEGDE'],
  },
  {
    id: 'taa.gez-alleen-vd',
    label: 'Alleen voltooid deelwoord als gezegde',
    module: 'Taalbegrip',
    description:
      'Leerling neemt alleen het voltooid deelwoord op in het gezegde en laat het hulpwerkwoord weg.',
    typicalFamilies: ['TAA-ONT-GEZEGDE'],
  },
  {
    id: 'taa.gez-werkwoorden-tellen-fout',
    label: 'Werkwoorden in gezegde verkeerd geteld',
    module: 'Taalbegrip',
    description:
      'Leerling telt het aantal werkwoorden in het werkwoordelijk gezegde verkeerd (te weinig of te veel).',
    typicalFamilies: ['TAA-ONT-GEZEGDE'],
  },
  {
    id: 'taa.gez-bijwoord-als-werkwoord',
    label: 'Bijwoord geteld als werkwoord in gezegde',
    module: 'Taalbegrip',
    description: 'Leerling telt een bijwoord mee als werkwoord in het gezegde.',
    typicalFamilies: ['TAA-ONT-GEZEGDE'],
  },
  {
    id: 'taa.gez-werkwoordelijk-als-naamwoordelijk',
    label: 'Werkwoordelijk gezegde aangezien voor naamwoordelijk',
    module: 'Taalbegrip',
    description:
      'Leerling classificeert een werkwoordelijk gezegde als naamwoordelijk (verwart hoofd- en koppelwerkwoord).',
    typicalFamilies: ['TAA-ONT-GEZEGDE'],
  },
  {
    id: 'taa.gez-koppelwerkwoord-niet-herkend',
    label: 'Koppelwerkwoord niet herkend',
    module: 'Taalbegrip',
    description:
      'Leerling herkent een koppelwerkwoord (zijn, worden, blijven, blijken, lijken, schijnen, heten, dunken, voorkomen) niet als zodanig.',
    typicalFamilies: ['TAA-ONT-GEZEGDE'],
  },
  // ─── TAA-ONT-VOORZETSEL — seed-reconciliatie (Phase B, juni 2026) ───────
  {
    id: 'taa.lidwoord-als-vz',
    label: 'Lidwoord verward met voorzetsel',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een lidwoord ("de", "het", "een") als voorzetsel in een voorzetselvoorwerp.',
    typicalFamilies: ['TAA-ONT-VOORZETSEL'],
  },
  {
    id: 'taa.zelfst-naamwoord-als-vz',
    label: 'Zelfstandig naamwoord verward met voorzetsel',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een zelfstandig naamwoord als voorzetsel in een voorzetselvoorwerp.',
    typicalFamilies: ['TAA-ONT-VOORZETSEL'],
  },
  {
    id: 'taa.werkwoord-als-vz',
    label: 'Werkwoord verward met voorzetsel',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een werkwoord als voorzetsel in een voorzetselvoorwerp.',
    typicalFamilies: ['TAA-ONT-VOORZETSEL'],
  },
  {
    id: 'taa.vzv-bwb-als-vzv',
    label: 'Bijwoordelijke bepaling verward met voorzetselvoorwerp',
    module: 'Taalbegrip',
    description:
      'Leerling markeert een bijwoordelijke bepaling (vervangbaar voorzetsel) als voorzetselvoorwerp.',
    typicalFamilies: ['TAA-ONT-VOORZETSEL'],
  },
  {
    id: 'taa.vzv-niet-vast-voorzetsel',
    label: 'Niet-vast voorzetsel aangezien voor vast',
    module: 'Taalbegrip',
    description:
      'Leerling beschouwt een vrij vervangbaar voorzetsel als vast bij het werkwoord en classificeert het zinsdeel onterecht als voorzetselvoorwerp.',
    typicalFamilies: ['TAA-ONT-VOORZETSEL'],
  },
  {
    id: 'taa.vzv-verkeerd-voorzetsel-bij-werkwoord',
    label: 'Verkeerd vast voorzetsel bij werkwoord',
    module: 'Taalbegrip',
    description:
      'Leerling koppelt het verkeerde vaste voorzetsel aan een werkwoord (bijv. "lijken aan" i.p.v. "lijken op").',
    typicalFamilies: ['TAA-ONT-VOORZETSEL'],
  },
  // ─── BWB tijd vs plaats/wijze — seed-upgrade (Phase B, juni 2026) ───────
  {
    id: 'taa.bwb-tijd-plaats-verward',
    label: 'Tijdsbepaling verward met plaatsbepaling',
    module: 'Taalbegrip',
    description:
      'Leerling beoordeelt een bijwoordelijke bepaling van plaats/richting ("naar de bus") ten onrechte als tijdsbepaling.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  {
    id: 'taa.bwb-tijd-wijze-verward',
    label: 'Tijdsbepaling verward met wijze-bepaling',
    module: 'Taalbegrip',
    description:
      'Leerling beoordeelt een bijwoordelijke bepaling van wijze ("prachtig", "snel") ten onrechte als tijdsbepaling.',
    typicalFamilies: ['TAA-ONT-BWB'],
  },
  // ─── TAA-ONT-BETREKKELIJK — aanvullingen (juni 2026) ────────────────────
  {
    id: 'taa.betrekkelijk-wat-verkeerd-gebruikt',
    label: '"Wat" verkeerd gebruikt als betrekkelijk voornaamwoord',
    module: 'Taalbegrip',
    description:
      'Leerling gebruikt "wat" als betrekkelijk voornaamwoord op een plek waar "die" of "dat" verplicht is (bijv. "de jongen wat fietst" i.p.v. "de jongen die fietst"). "Wat" is alleen geldig bij onbepaalde antecedenten (alles, iets, niets) of overtreffende trappen.',
    typicalFamilies: ['TAA-ONT-BETREKKELIJK'],
  },
]
